// DOM HUD overlay (Fase 2) — independent of canvas aspect / safe-area aware.

import { G } from '../core/state';
import { PLAYERW, PLAYERH, TILE } from '../core/constants';
import { isDomHud, isGpuRenderer } from '../render/render-config';
import { resolveTilePrompt, consumeTimedSpecialMessage } from '../core/special-tile-prompt';
const tr = Math.trunc;

const METER_SPRITE = './assets/i/v2/meter.png';
const CIRCUIT_SPRITE = './assets/i/v2/circuit-bar.png';
const METER_CELL_PX = 12;
const METER_SEGMENTS = 25;
const CIRCUIT_BAR_W = 320;
const CIRCUIT_ROW_PX = 12;

let root: HTMLDivElement | null = null;
let statsEl: HTMLDivElement | null = null;
let metersEl: HTMLDivElement | null = null;
let livesEl: HTMLDivElement | null = null;
let hpEl: HTMLDivElement | null = null;
let circuitEl: HTMLDivElement | null = null;
let pauseEl: HTMLDivElement | null = null;
let timerEl: HTMLDivElement | null = null;
let dialogEl: HTMLDivElement | null = null;
let teleEl: HTMLDivElement | null = null;
let promptEl: HTMLDivElement | null = null;
function upgradePrice(t: number): number {
  switch (t) {
    case 0:
      return (100 - G.training * 50) * G.player_shield + (5 << G.player_shield) * (5 - G.training * 2);
    case 1:
      return (80 - G.training * 40) * G.circuit_fillrate + (5 << G.circuit_fillrate) * (4 - G.training * 2);
    case 2:
      return (80 - G.training * 40) * G.circuit_recoverrate + (5 << G.circuit_recoverrate) * (4 - G.training * 2);
    default:
      return 123;
  }
}

function meterBar(label: string, value: number, max: number, highlight: boolean, t: number): string {
  const cells = Math.min(max, value);
  const fillW = cells * METER_CELL_PX;
  const trackW = max * METER_CELL_PX;
  const color = highlight ? `hsl(${200 + (t % 13) * 10}, 80%, 72%)` : '#c8cad8';
  const upgradeClass = highlight ? ' merit-hud-meter--upgrade' : '';
  return `<div class="merit-hud-meter${upgradeClass}">
    <span class="merit-hud-meter-label" style="color:${color}">${label}</span>
    <span class="merit-hud-pixel-meter" style="width:${trackW}px;--fill:${fillW}px">
      <span class="merit-hud-pixel-meter-fill"></span>
    </span>
  </div>`;
}

function circuitBar(pct: number, positive: boolean, valueText: string): string {
  const fillPct = Math.min(100, Math.max(0, pct * 100));
  let sign = '';
  if (positive && pct > 0) sign = '+';
  else if (!positive && pct > 0) sign = '-';
  const tone = positive ? 'merit-circuit-bar--pos' : 'merit-circuit-bar--neg';
  const valueClass = positive ? '' : ' merit-circuit-value--neg';
  return `<div class="merit-circuit-wrap">
    <span class="merit-circuit-label">Psi Circuit <span class="merit-circuit-value${valueClass}">${sign}${valueText}</span></span>
    <div class="merit-circuit-bar ${tone}">
      <div class="merit-circuit-fill" style="width:${fillPct}%"></div>
    </div>
  </div>`;
}
export function initHud(parent: HTMLElement): void {
  if (root || (!isDomHud() && !isGpuRenderer())) return;
  root = document.createElement('div');
  root.id = 'merit-hud';
  Object.assign(root.style, {
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',
    zIndex: '15',
    fontFamily: '"Courier New", Courier, monospace',
    fontSize: 'clamp(10px, 2vmin, 14px)',
    color: '#ddd',
    lineHeight: '1.35',
  } as CSSStyleDeclaration);

  const style = document.createElement('style');
  style.textContent = `
    #merit-hud .merit-hud-panel {
      background: rgba(16, 18, 28, 0.88);
      border: 1px solid rgba(88, 96, 128, 0.55);
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 2px 8px rgba(0, 0, 0, 0.35);
      padding: 6px 10px;
      border-radius: 2px;
    }
    #merit-hud .merit-hud-top {
      position: absolute;
      top: max(6px, env(safe-area-inset-top, 0px));
      left: max(6px, env(safe-area-inset-left, 0px));
      right: max(6px, env(safe-area-inset-right, 0px));
      display: flex;
      justify-content: space-between;
      gap: 8px;
      align-items: flex-start;
    }
    #merit-hud .merit-hud-meters {
      flex: 1;
      text-align: center;
      max-width: 420px;
      margin: 0 auto;
      padding: 4px 8px;
    }
    #merit-hud .merit-hud-meter {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin: 3px 0;
    }
    #merit-hud .merit-hud-meter-label {
      width: 52px;
      text-align: right;
      font-size: 0.92em;
      letter-spacing: 0.04em;
      text-shadow: 0 1px 0 #000;
    }
    #merit-hud .merit-hud-pixel-meter {
      position: relative;
      height: ${METER_CELL_PX}px;
      flex-shrink: 0;
      image-rendering: pixelated;
      image-rendering: crisp-edges;
      background: url('${METER_SPRITE}') 0 -${METER_CELL_PX}px / 360px ${METER_CELL_PX * 2}px no-repeat;
    }
    #merit-hud .merit-hud-pixel-meter-fill {
      position: absolute;
      left: 0;
      top: 0;
      height: ${METER_CELL_PX}px;
      width: var(--fill, 0px);
      overflow: hidden;
    }
    #merit-hud .merit-hud-pixel-meter-fill::after {
      content: '';
      display: block;
      width: ${METER_SEGMENTS * METER_CELL_PX}px;
      height: ${METER_CELL_PX}px;
      background: url('${METER_SPRITE}') 0 0 / 360px ${METER_CELL_PX * 2}px no-repeat;
      image-rendering: pixelated;
      image-rendering: crisp-edges;
    }
    #merit-hud .merit-hud-meter--upgrade .merit-hud-pixel-meter-fill::after {
      filter: brightness(1.18) saturate(1.25);
    }
    #merit-hud .merit-circuit-wrap {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      flex-wrap: wrap;
    }
    #merit-hud .merit-circuit-label {
      font-size: 0.95em;
      letter-spacing: 0.05em;
      text-shadow: 0 1px 0 #000;
      white-space: nowrap;
    }
    #merit-hud .merit-circuit-value {
      color: #e8ecf8;
      font-variant-numeric: tabular-nums;
    }
    #merit-hud .merit-circuit-value--neg {
      color: #ffb088;
    }
    #merit-hud .merit-hud-prompt {
      position: absolute;
      left: 50%;
      top: 22%;
      transform: translateX(-50%);
      max-width: min(92vw, 520px);
      text-align: center;
      padding: 10px 18px;
      display: none;
      pointer-events: none;
      font-size: clamp(11px, 2.2vmin, 15px);
      letter-spacing: 0.03em;
      line-height: 1.4;
      text-shadow: 0 1px 0 #000;
    }
    #merit-hud .merit-hud-prompt--dim {
      opacity: 0.78;
    }
    #merit-hud .merit-circuit-bar {
      position: relative;
      width: min(${CIRCUIT_BAR_W}px, 58vw);
      height: ${CIRCUIT_ROW_PX}px;
      image-rendering: pixelated;
      image-rendering: crisp-edges;
      background: url('${CIRCUIT_SPRITE}') 0 0 / ${CIRCUIT_BAR_W}px ${CIRCUIT_ROW_PX * 3}px no-repeat;
    }
    #merit-hud .merit-circuit-fill {
      position: absolute;
      left: 2px;
      top: 2px;
      bottom: 2px;
      max-width: calc(100% - 4px);
      overflow: hidden;
      image-rendering: pixelated;
      image-rendering: crisp-edges;
    }
    #merit-hud .merit-circuit-bar--pos .merit-circuit-fill::after,
    #merit-hud .merit-circuit-bar--neg .merit-circuit-fill::after {
      content: '';
      display: block;
      width: ${CIRCUIT_BAR_W - 4}px;
      height: ${CIRCUIT_ROW_PX - 4}px;
      image-rendering: pixelated;
      image-rendering: crisp-edges;
    }
    #merit-hud .merit-circuit-bar--pos .merit-circuit-fill::after {
      background: url('${CIRCUIT_SPRITE}') -2px -${CIRCUIT_ROW_PX + 2}px / ${CIRCUIT_BAR_W}px ${CIRCUIT_ROW_PX * 3}px no-repeat;
    }
    #merit-hud .merit-circuit-bar--neg .merit-circuit-fill::after {
      background: url('${CIRCUIT_SPRITE}') -2px -${CIRCUIT_ROW_PX * 2 + 2}px / ${CIRCUIT_BAR_W}px ${CIRCUIT_ROW_PX * 3}px no-repeat;
    }
    #merit-hud .merit-hud-bottom {
      position: absolute;
      bottom: max(6px, env(safe-area-inset-bottom, 0px));
      left: max(6px, env(safe-area-inset-left, 0px));
      right: max(6px, env(safe-area-inset-right, 0px));
      display: flex;
      justify-content: center;
    }
    #merit-hud .merit-hud-overlay {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      padding: 16px 24px;
      background: rgba(0,0,0,0.75);
      border: 2px solid #888;
      display: none;
    }
    #merit-hud .merit-hud-heart { color: #e55; }
    #merit-hud .merit-hud-life { color: #ee8; }
  `;
  document.head.appendChild(style);

  promptEl = document.createElement('div');
  promptEl.className = 'merit-hud-panel merit-hud-prompt';
  promptEl.id = 'merit-hud-prompt';
  root.appendChild(promptEl);

  if (!isDomHud()) {
    parent.appendChild(root);
    root.style.display = 'none';
    return;
  }

  statsEl = document.createElement('div');
  statsEl.className = 'merit-hud-panel';
  metersEl = document.createElement('div');
  metersEl.className = 'merit-hud-panel merit-hud-meters';
  const rightCol = document.createElement('div');
  rightCol.className = 'merit-hud-panel';
  livesEl = document.createElement('div');
  hpEl = document.createElement('div');
  rightCol.appendChild(livesEl);
  rightCol.appendChild(hpEl);

  const top = document.createElement('div');
  top.className = 'merit-hud-top';
  top.appendChild(statsEl);
  top.appendChild(metersEl);
  top.appendChild(rightCol);

  circuitEl = document.createElement('div');
  circuitEl.className = 'merit-hud-panel merit-hud-bottom';

  pauseEl = document.createElement('div');
  pauseEl.className = 'merit-hud-overlay';
  pauseEl.id = 'merit-hud-pause';
  dialogEl = document.createElement('div');
  dialogEl.className = 'merit-hud-overlay';
  dialogEl.id = 'merit-hud-quit';
  teleEl = document.createElement('div');
  teleEl.className = 'merit-hud-overlay';
  teleEl.id = 'merit-hud-tele';

  root.appendChild(top);
  root.appendChild(circuitEl);
  root.appendChild(pauseEl);
  root.appendChild(dialogEl);
  root.appendChild(teleEl);
  parent.appendChild(root);
}

export function destroyHud(): void {
  root?.remove();
  root = null;
}

function resetHudOverlays(): void {
  if (pauseEl) pauseEl.style.display = 'none';
  if (dialogEl) dialogEl.style.display = 'none';
  if (teleEl) teleEl.style.display = 'none';
  if (promptEl) promptEl.style.display = 'none';
  timerEl?.remove();
}

export function showGameHud(): void {
  if (!root || (!isDomHud() && !isGpuRenderer())) return;
  root.style.display = '';
}

export function hideGameHud(): void {
  if (!root) return;
  resetHudOverlays();
  root.style.display = 'none';
}

export function updateSpecialTilePrompt(t: number): void {
  if (!root || (!isDomHud() && !isGpuRenderer()) || !promptEl) return;
  if (G.tele_select || G.map_enabled || G.voluntary_exit || (G.game_paused && !G.map_enabled)) {
    promptEl.style.display = 'none';
    return;
  }

  const tileX = tr((G.player_x + PLAYERW / 2) / TILE);
  const tileY = tr((G.player_y + PLAYERH / 2) / TILE);
  let message = '';
  if (G.specialmessage !== 0) {
    message = consumeTimedSpecialMessage();
  } else {
    message = resolveTilePrompt(tileX, tileY);
  }
  if (message === '') {
    promptEl.style.display = 'none';
    return;
  }

  promptEl.textContent = message;
  promptEl.style.display = 'block';
  promptEl.classList.toggle('merit-hud-prompt--dim', t % 16 >= 8);
}

export function updateHud(t: number): void {
  updateSpecialTilePrompt(t);
  if (!root || !isDomHud()) return;

  if (G.tele_select) {
    teleEl!.style.display = 'block';
    teleEl!.textContent =
      'Use movement keys to locate a checkpoint.\nPress ENTER to teleport. ESCAPE or TAB when done.';
    statsEl!.style.display = 'none';
    metersEl!.style.display = 'none';
  } else {
    teleEl!.style.display = 'none';
    statsEl!.style.display = 'block';
    metersEl!.style.display = 'block';

    statsEl!.innerHTML = [
      `Psi Crystals: <b>${G.player_gems}</b>`,
      `Explored: <b>${(G.explored / 30.0).toFixed(1)}%</b> (${G.explored}/3000)`,
      `Cleared: <b>${((G.killed_enemies / G.total_enemies) * 100).toFixed(1)}%</b> (${G.killed_enemies}/${G.total_enemies})`,
    ].join('<br>');

    metersEl!.innerHTML = [
      meterBar(
        'Shield',
        G.player_shield,
        METER_SEGMENTS,
        G.player_gems >= upgradePrice(0) && G.player_shield !== 30,
        t
      ),
      meterBar(
        'Charge',
        G.circuit_fillrate,
        METER_SEGMENTS,
        G.player_gems >= upgradePrice(1) && G.circuit_fillrate !== 30,
        t
      ),
      meterBar(
        'Refill',
        G.circuit_recoverrate,
        METER_SEGMENTS,
        G.player_gems >= upgradePrice(2) && G.circuit_recoverrate !== 30,
        t
      ),
    ].join('');
  }

  if (!G.training && livesEl) {
    let lifeText = '<span class="merit-hud-life">♥</span> ';
    lifeText += G.player_lives <= 99 ? String(G.player_lives) : '**';
    const partPct = tr((G.player_lives_part * 100) / 88);
    lifeText += `<div style="width:48px;height:4px;background:#222;margin-top:2px"><div style="width:${partPct}%;height:100%;background:#a96"></div></div>`;
    livesEl.innerHTML = lifeText;
  } else if (livesEl) {
    livesEl.innerHTML = '';
  }

  if (hpEl) {
    let hp = '';
    const heart = '<span class="merit-hud-heart">♥</span>';
    const half = '<span class="merit-hud-heart">♡</span>';
    if (G.player_shield !== 30) {
      for (let i = 0; i < G.player_hp; i++) hp += heart;
    } else {
      for (let i = 0; i < tr(G.player_hp / 2); i++) hp += heart;
      if (G.player_hp % 2 === 1) hp += half;
    }
    hpEl.innerHTML = hp;
  }

  if (circuitEl) {
    const pct = G.circuit_size > 0 ? Math.abs(G.magic_circuit) / G.circuit_size : 0;
    const valueText = (Math.abs(G.magic_circuit) / 100).toFixed(1);
    circuitEl.innerHTML = circuitBar(pct, G.magic_circuit > 0, valueText);
  }

  if (G.game_paused && !G.map_enabled && !G.voluntary_exit) {
    pauseEl!.style.display = 'block';
    pauseEl!.textContent = 'Paused';
    const sec = tr(G.expired_ms / 1000) % 60;
    const min = tr(tr(G.expired_ms / 1000) / 60) % 60;
    const hr = tr(tr(tr(G.expired_ms / 1000) / 60) / 60) % 24;
    const day = tr(tr(tr(G.expired_ms / 1000) / 60) / 60 / 24);
    let tbuf = day > 0 ? `${day}d ${hr}h ${min}m ${sec}s` : hr > 0 ? `${hr}h ${min}m ${sec}s` : `${min}m ${sec}s`;
    timerEl ??= document.createElement('div');
    timerEl.style.cssText = 'position:absolute;bottom:max(36px,env(safe-area-inset-bottom));right:max(8px,env(safe-area-inset-right));';
    timerEl.textContent = tbuf;
    if (!timerEl.parentElement) root!.appendChild(timerEl);
  } else {
    pauseEl!.style.display = 'none';
    timerEl?.remove();
  }

  if (G.voluntary_exit) {
    dialogEl!.style.display = 'block';
    dialogEl!.innerHTML = 'Are you sure you want to quit?<br><br>Press ENTER to confirm.';
  } else {
    dialogEl!.style.display = 'none';
  }
}

export function isHudDomActive(): boolean {
  return isDomHud() && root !== null;
}

export function isHudPromptActive(): boolean {
  return (isDomHud() || isGpuRenderer()) && root !== null;
}
